import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { port, GlobalEnquiryContext } from '../App';
import HomeNavBar from '../Component/HomeComponent/HomeNavBar';

import ExecutionFramework from "../Component/Services/ExecutionFramework";
import OutcomeMetrics from "../Component/Services/OutcomeMetrics";
import ServiceCategories from "../Component/Services/ServiceCategories";
import ServiceFinalCTA from "../Component/Services/ServiceFinalCTA";
import ServiceHero from "../Component/Services/ServiceHero";
import ServiceDifferentiators from "../Component/Services/ServiceDifferentiators";
import IndustryExpertise from "../Component/Services/IndustryExpertise";
import MainServicesPage from "../Component/Services/MainServicesPage";
import CTAButton from '../Component/CTAButton';
import recruitmentImg from '../Assest/cards/recruitment-consulting-firm.avif';

const staffingStaticData = {
    title: "Staffing & Recruitment Services",
    slug: "staffing",
    meta_title: "AI-Powered Staffing & Recruitment Services | Merida HR Consulting",
    meta_description: "Partner with Merida HR Consulting for professional staffing services, permanent recruitment, IT staffing, non-IT staffing, and bulk hiring solutions in India.",

    // Hero Section
    hero_title: "AI-Powered Staffing & Recruitment Services",
    hero_subtitle: "Built To Help Businesses Scale Faster With Smarter Hiring Systems",
    hero_description: "We combine AI-powered talent sourcing, specialized recruiters, workforce intelligence systems, and scalable hiring frameworks to help organizations hire smarter, faster, and more efficiently.",
    hero_primary_cta: "Book Free Hiring Strategy Consultation",
    hero_secondary_cta: "Explore Recruitment Solutions",
    hero_image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=800&q=80",

    // Enquiry Form Metadata
    enquiry_title: "Speak With Staffing & Recruitment Specialists",
    enquiry_description: "Every business operates with different workforce structures, hiring priorities, expansion goals, and operational challenges. Discuss your requirements with our recruitment specialists today.",
    enquiry_cta: "Request Recruitment Consultation",
    enquiry_fields: [
        "Full Name",
        "Company Name",
        "Business Email",
        "Phone Number",
        "Industry Type",
        "Hiring Requirement",
        "Number Of Positions",
        "Hiring Timeline",
        "Business Location",
        "Message"
    ],

    // Categories
    categories_title: "Explore Our Specialized Staffing & Recruitment Services",
    categories_list: [
        {
            title: "Contract Staffing Services",
            micro_heading: "Flexible Workforce Solutions For Dynamic Business Demands",
            description: <>Businesses often require workforce flexibility to support temporary projects, operational surges, seasonal demands, technology implementations, workforce transitions, and urgent execution requirements. Our <a href="/blog/contract-staffing-services-guide">Contract Staffing Services</a> help organizations deploy skilled professionals quickly without long-term hiring complexity.</>,
            points: [
                "Reduce workforce dependency gaps",
                "Improve workforce agility",
                "Scale project teams faster",
                "Support temporary operational requirements",
                "Access specialized professionals rapidly",
                "Improve short-term workforce efficiency"
            ]
        },
        {
            title: "Permanent Staffing Services",
            micro_heading: "Strategic Hiring Solutions Designed For Long-Term Workforce Stability",
            description: <>Strong businesses require stable teams capable of contributing to long-term organizational growth. Discover how <a href="/blog/permanent-staffing-strategies">permanent staffing strategies</a> improve employee retention and workforce stability.</>,
            points: [
                "Improve long-term workforce retention",
                "Reduce hiring inefficiencies",
                "Build stable operational teams",
                "Strengthen workforce quality",
                "Hire leadership and mid-level professionals",
                "Scale departments strategically"
            ]
        },
        {
            title: "IT Staffing Services",
            micro_heading: "Specialized Technology Recruitment Across Modern Digital Ecosystems",
            description: "Technology hiring requires specialized recruitment expertise, technical understanding, faster sourcing capabilities, and access to highly niche talent networks.",
            points: [
                "Reduce technical hiring delays",
                "Access pre-screened tech professionals",
                "Improve workforce quality",
                "Scale engineering teams faster",
                "Support digital transformation hiring",
                "Build specialized technical teams"
            ]
        },
        {
            title: "Non-IT Staffing Services",
            micro_heading: "Operational Workforce Solutions For Core Business Functions",
            description: "Operational efficiency depends heavily on stable non-technical workforce execution across business-critical departments.",
            points: [
                "Improve operational workforce continuity",
                "Strengthen department productivity",
                "Reduce operational hiring delays",
                "Access role-ready professionals",
                "Scale workforce operations efficiently",
                "Improve workforce stability"
            ]
        },
        {
            title: "Bulk Hiring Services",
            micro_heading: "High-Volume Recruitment Systems Built For Rapid Workforce Expansion",
            description: "Rapid business expansion often creates high-volume recruitment requirements that traditional hiring systems struggle to manage efficiently.",
            points: [
                "Accelerate workforce expansion",
                "Maintain hiring quality at scale",
                "Improve recruitment coordination",
                "Reduce bulk hiring delays",
                "Support aggressive growth plans",
                "Improve workforce deployment efficiency"
            ]
        }
    ],

    // Differentiators
    differentiators_title: "Why Businesses Choose Merida HR For Staffing & Recruitment Services",
    differentiators_list: [
        {
            title: "AI-Powered Sourcing Infrastructure",
            description: "We leverage AI-supported sourcing systems, recruitment intelligence platforms, structured screening workflows, and talent analytics tools to improve hiring efficiency across every recruitment stage. This helps businesses: Improve candidate discovery, Reduce sourcing delays, Accelerate workforce deployment, Improve recruitment productivity, Enhance hiring visibility."
        },
        {
            title: "Specialized Industry Recruiters",
            description: "Unlike generic recruitment agencies, our recruiters specialize in industry-focused workforce ecosystems across: IT & Software, Healthcare, Manufacturing, BFSI, Ecommerce, Logistics, Startups, GCC ecosystems. This enables businesses to access more relevant talent pipelines and better hiring outcomes."
        },
        {
            title: "Faster Recruitment Turnaround",
            description: "Delayed recruitment directly impacts operations, productivity, and business continuity. Our structured recruitment systems help organizations: Reduce hiring bottlenecks, Improve interview coordination, Accelerate candidate shortlisting, Manage urgent hiring efficiently, Improve hiring execution speed."
        },
        {
            title: "Scalable Workforce Expansion Support",
            description: "Whether businesses are scaling operations, launching new departments, entering new markets, or building GCCs, our workforce systems are structured to support sustainable organizational growth without recruitment chaos."
        },
        {
            title: "Access To Pre-Screened Talent Networks",
            description: "Our extensive sourcing systems and talent databases help businesses connect with role-ready professionals faster across technical, operational, leadership, and industry-specific hiring categories."
        }
    ],

    // Process Framework
    process_title: "Our Strategic Recruitment Execution Framework",
    process_list: [
        {
            title: "Workforce Discovery & Hiring Assessment",
            description: "Understanding hiring goals, workforce gaps, business priorities, operational challenges, and workforce planning requirements."
        },
        {
            title: "Recruitment Strategy Planning",
            description: "Building customized recruitment frameworks aligned with hiring urgency, workforce structure, and business objectives."
        },
        {
            title: "AI-Powered Talent Sourcing",
            description: <>We leverage AI-supported sourcing systems to improve hiring efficiency. Learn how <a href="https://www.phenom.com/blog/recruiting-ai-guide">AI in recruitment</a> is transforming candidate sourcing and workforce planning.</>
        },
        {
            title: "Screening & Candidate Evaluation",
            description: <>"Structured evaluation based on technical capability and role alignment. Read our guide on <a href="https://hr.uw.edu/talent/hiring-process/interviewing/candidates-evaluation-tips-and-guidelines/">candidate screening best practices</a> to improve hiring quality.</>
        },
        {
            title: "Interview Coordination & Hiring Management",
            description: "Managing scheduling workflows, stakeholder communication, recruitment tracking, and hiring coordination systems."
        },
        {
            title: "Offer Rollout & Workforce Onboarding",
            description: "Supporting onboarding coordination, documentation workflows, and workforce transition processes."
        },
        {
            title: "Workforce Scalability Support",
            description: "Continuous recruitment support for future workforce expansion, operational scaling, and long-term hiring efficiency."
        }
    ],

    // Industry Expertise
    industry_title: "Industry-Focused Staffing & Recruitment Expertise",
    industry_description: "Different industries require different hiring models, workforce strategies, recruitment timelines, and sourcing ecosystems. Our recruitment specialists understand industry-specific workforce demands deeply — enabling businesses to access more relevant talent pools and stronger hiring outcomes.",
    industry_list: [
        {
            title: "IT & Software",
            description: "Technology recruitment across engineering, AI, cybersecurity, cloud, DevOps, ERP, and enterprise systems."
        },
        {
            title: "Healthcare",
            description: "Healthcare workforce solutions for hospitals, diagnostics, healthcare operations, and medical administration."
        },
        {
            title: "Manufacturing",
            description: "Industrial workforce recruitment, operational staffing, plant hiring, and production workforce deployment."
        },
        {
            title: "BFSI",
            description: "Banking, insurance, finance operations, fintech, and compliance workforce hiring support."
        },
        {
            title: "Ecommerce & Retail",
            description: "Scalable workforce hiring for ecommerce operations, warehousing, retail support, and customer experience teams."
        },
        {
            title: "Logistics & Supply Chain",
            description: "Operational staffing solutions for logistics ecosystems and supply chain workforce management."
        },
        {
            title: "Startups",
            description: "Agile recruitment systems designed for rapidly growing startup environments."
        },
        {
            title: "GCCs",
            description: "Strategic staffing support for Global Capability Center workforce scaling and offshore expansion."
        }
    ],

    // Outcomes
    outcomes_title: "Recruitment Services Built Around Measurable Hiring Outcomes",
    outcomes_list: [
        {
            title: "Faster Hiring Cycles",
            description: "Reduce recruitment turnaround through optimized sourcing and structured hiring workflows."
        },
        {
            title: "Better Workforce Quality",
            description: "Access technically evaluated and operationally aligned professionals faster."
        },
        {
            title: "Workforce Scalability",
            description: "Support rapid expansion without compromising recruitment efficiency."
        },
        {
            title: "Reduced Hiring Costs",
            description: "Improve hiring productivity through structured recruitment operations."
        },
        {
            title: "Better Candidate Experience",
            description: "Streamlined communication and smoother recruitment journeys."
        },
        {
            title: "Long-Term Workforce Stability",
            description: "Build stronger teams aligned with operational and business goals."
        }
    ],

    // Final CTA
    final_cta_title: "Ready To Build Smarter Workforce Systems?",
    final_cta_description: "Partner with a highly experienced Staffing & Recruitment Services company delivering scalable hiring solutions, AI-powered recruitment systems, contract staffing, permanent hiring, workforce expansion support, and specialized talent acquisition solutions for modern businesses.",
    final_cta_primary: "Book Hiring Strategy Session",
    final_cta_secondary: "Get Custom Recruitment Plan"
};

// Static content for Specialized Recruitment Services
const specializedRecruitmentStaticData = {
    title: "Specialized Recruitment Services",
    slug: "specialized-recruitment",
    meta_title: "Specialized Recruitment Services | Merida HR Consulting",
    meta_description: "Hire critical talent faster and smarter with Merida's specialized recruitment services, executive search, startup hiring, remote solutions, and GCC talent acquisition.",

    // Hero Section
    hero_title: "Specialized Recruitment Services",
    hero_subtitle: "Hire Critical Talent Faster, Smarter & More Strategically",
    hero_description: "Combining AI-powered sourcing intelligence, specialized recruiters, global talent access, structured hiring frameworks, and industry-driven recruitment execution to help businesses secure highly qualified professionals for critical business functions.",
    hero_primary_cta: "Book Specialized Hiring Consultation",
    hero_secondary_cta: "Explore Recruitment Solutions",
    hero_image: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=800&q=80",

    // Enquiry Form Metadata
    enquiry_title: "Speak With Specialized Recruitment Consultants",
    enquiry_description: "Every specialized hiring requirement demands a different recruitment strategy, sourcing ecosystem, evaluation model, and workforce planning approach. Discuss your requirements with our recruitment specialists today.",
    enquiry_cta: "Request Specialized Recruitment Consultation",
    enquiry_fields: [
        "Full Name",
        "Company Name",
        "Business Email",
        "Phone Number",
        "Industry Type",
        "Hiring Requirement",
        "Team Size",
        "Business Location",
        "Hiring Timeline",
        "Message"
    ],

    // Categories
    categories_title: "Explore Our Specialized Recruitment Services",
    categories_list: [
        {
            title: "Executive Search Services",
            micro_heading: "Leadership Hiring Solutions Built For Business-Critical Roles",
            description: "Leadership hiring directly impacts organizational growth, operational strategy, decision-making capability, and long-term business performance. Our Executive Search Services help organizations identify and recruit highly experienced leadership professionals through confidential, research-driven, and strategically structured recruitment processes.",
            points: [
                "C-Level executives",
                "Department heads",
                "Senior management professionals",
                "Strategic leadership roles",
                "Business transformation leaders",
                "Operational leadership talent"
            ],
            cta_text: "Explore Executive Search Services"
        },
        {
            title: "GCC Hiring Solutions",
            micro_heading: "Specialized Workforce Expansion Support For Global Capability Centers",
            description: <>GCC workforce expansion requires highly scalable recruitment systems capable of supporting rapid operational growth. Explore the best practices for <a href="/blog/gcc-hiring-india-guide"> GCC hiring in India</a> and building scalable offshore teams.</>,
            points: [
                "Scale offshore teams efficiently",
                "Build operational workforce structures",
                "Hire technical and non-technical professionals",
                "Improve hiring scalability",
                "Support global business operations",
                "Accelerate workforce deployment"
            ],
            cta_text: "Explore GCC Hiring Services"
        },
        {
            title: "Startup Hiring Services",
            micro_heading: "Agile Recruitment Systems Designed For Fast-Growing Startups",
            description: "Startup environments demand faster hiring, lean workforce scaling, multi-functional talent acquisition, and highly adaptable recruitment execution. Our Startup Hiring Services help startups:",
            points: [
                "Build high-performing teams quickly",
                "Reduce hiring delays",
                "Scale workforce operations strategically",
                "Hire adaptable professionals",
                "Improve workforce agility",
                "Support rapid expansion plans"
            ],
            cta_text: "Explore Startup Hiring Services"
        },
        {
            title: "Campus Recruitment Services",
            micro_heading: "Structured Fresher Hiring Solutions For Workforce Pipeline Development",
            description: "Strong workforce planning requires continuous access to emerging talent pipelines. Our Campus Recruitment Services help organizations build fresher workforce ecosystems through strategic campus engagement, structured screening processes, and scalable graduate hiring programs. We help businesses:",
            points: [
                "Conduct large-scale campus drives",
                "Build fresher hiring pipelines",
                "Improve graduate workforce onboarding",
                "Access young talent pools",
                "Strengthen workforce succession planning",
                "Scale entry-level recruitment efficiently"
            ],
            cta_text: "Explore Campus Recruitment Services"
        },
        {
            title: "Remote Hiring Solutions",
            micro_heading: "Distributed Workforce Hiring For Modern Business Environments",
            description: <>Remote work models require highly structured hiring systems capable of identifying professionals with strong communication, accountability, collaboration, and remote execution capabilities.Explore the latest <a href="/blog/remote-hiring-best-practices">remote hiring best practices for distributed teams</a>. Our Remote Hiring Solutions help businesses:</>,
            points: [
                "Build distributed workforce teams",
                "Hire remote-ready professionals",
                "Expand global workforce access",
                "Reduce geographical hiring limitations",
                "Improve workforce flexibility",
                "Scale remote operations efficiently"
            ],
            cta_text: "Explore Remote Hiring Solutions"
        },
        {
            title: "Offshore Recruitment Services",
            micro_heading: "International Workforce Hiring Support For Global Expansion",
            description: "Businesses expanding internationally require strategic offshore recruitment systems capable of supporting global workforce acquisition, cross-border hiring, and operational scalability. Our Offshore Recruitment Services help organizations:",
            points: [
                "Build offshore workforce teams",
                "Access international talent pools",
                "Improve hiring scalability",
                "Support operational expansion",
                "Reduce hiring limitations",
                "Strengthen workforce diversity"
            ],
            cta_text: "Explore Offshore Recruitment Services"
        },
        {
            title: "Virtual Recruitment Services",
            micro_heading: "Digitally Enabled Recruitment Systems For Faster Hiring Execution",
            description: <>Modern businesses increasingly require digitally managed recruitment workflows. Discover how<a href="https://www.thomas.co/resources/type/hr-blog/virtual-recruiting-what-you-need-know"> virtual recruitment</a> improves hiring speed, candidate experience, and recruitment efficiency:</>,
            points: [
                "Streamline recruitment coordination",
                "Improve hiring accessibility",
                "Conduct virtual interviews efficiently",
                "Accelerate recruitment execution",
                "Improve candidate communication",
                "Reduce operational hiring delays"
            ],
            cta_text: "Explore Virtual Recruitment Services"
        },
        {
            title: "AI Recruitment Solutions",
            micro_heading: "AI-Driven Recruitment Systems Designed For Smarter Talent Acquisition",
            description: "Traditional recruitment systems often struggle with slow sourcing cycles, weak candidate matching, inconsistent screening quality, and poor recruitment scalability. Our AI Recruitment Solutions leverage recruitment intelligence technologies, automation workflows, predictive sourcing systems, and AI-supported screening frameworks to help businesses:",
            points: [
                "Improve sourcing precision",
                "Accelerate candidate discovery",
                "Reduce hiring delays",
                "Improve recruitment productivity",
                "Enhance hiring visibility",
                "Scale workforce operations efficiently"
            ],
            cta_text: "Explore AI Recruitment Solutions"
        }
    ],

    // Differentiators
    differentiators_title: "Why Choose Merida HR Consulting For Specialized Recruitment Services?",
    differentiators_list: [
        {
            title: "Specialized Recruitment Intelligence",
            description: "We leverage highly targeted sourcing systems, workforce intelligence tools, niche hiring frameworks, and specialized recruitment workflows to help businesses access hard-to-find professionals faster and more efficiently."
        },
        {
            title: "Leadership & Niche Talent Access",
            description: "Our recruitment ecosystem provides access to leadership professionals, specialized technical experts, startup-ready candidates, remote workforce talent, and offshore professionals across multiple industries and workforce categories."
        },
        {
            title: "Scalable Workforce Expansion Support",
            description: "Whether businesses are scaling startups, expanding GCCs, building global teams, or strengthening leadership structures, our workforce systems are built to support sustainable growth without recruitment inefficiencies."
        },
        {
            title: "AI-Powered Recruitment Infrastructure",
            description: "We combine AI-supported sourcing intelligence, structured evaluation systems, recruitment automation workflows, and workforce analytics to improve hiring speed, scalability, and candidate quality."
        }
    ],

    // Process Framework
    process_title: "Our Specialized Recruitment Execution Process",
    process_list: [
        {
            title: "Workforce Requirement Discovery",
            description: "Understanding hiring priorities, workforce structures, business goals, and specialized recruitment requirements."
        },
        {
            title: "Recruitment Strategy Architecture",
            description: "Designing customized recruitment workflows aligned with workforce complexity and hiring objectives."
        },
        {
            title: "Specialized Talent Sourcing",
            description: "Leveraging advanced sourcing intelligence systems and niche recruitment channels."
        },
        {
            title: "Structured Candidate Evaluation",
            description: <>"Screening candidates based on capability, experience relevance, strategic alignment, and workforce compatibility using proven<a href="https://www.thomas.co/resources/type/hr-blog/candidate-assessments-evaluation-tools" > candidate assessment techniques</a>.</>,
        },
        {
            title: "Interview & Hiring Coordination",
            description: "Managing communication workflows, stakeholder coordination, and recruitment execution."
        },
        {
            title: "Workforce Deployment Support",
            description: "Supporting onboarding coordination, workforce transition, and hiring completion processes."
        }
    ],

    // Industry Expertise
    industry_title: "Industry-Focused Staffing & Recruitment Expertise",
    industry_description: "Different industries require different hiring models, workforce strategies, recruitment timelines, and sourcing ecosystems. Our recruitment specialists understand industry-specific workforce demands deeply — enabling businesses to access more relevant talent pools and stronger hiring outcomes.",
    industry_list: [
        {
            title: "IT & Software",
            description: "Technology recruitment across engineering, AI, cybersecurity, cloud, DevOps, ERP, and enterprise systems."
        },
        {
            title: "Healthcare",
            description: "Healthcare workforce solutions for hospitals, diagnostics, healthcare operations, and medical administration."
        },
        {
            title: "Manufacturing",
            description: "Industrial workforce recruitment, operational staffing, plant hiring, and production workforce deployment."
        },
        {
            title: "BFSI",
            description: "Banking, insurance, finance operations, fintech, and compliance workforce hiring support."
        },
        {
            title: "Ecommerce & Retail",
            description: "Scalable workforce hiring for ecommerce operations, warehousing, retail support, and customer experience teams."
        },
        {
            title: "Logistics & Supply Chain",
            description: "Operational staffing solutions for logistics ecosystems and supply chain workforce management."
        },
        {
            title: "Startups",
            description: "Agile recruitment systems designed for rapidly growing startup environments."
        },
        {
            title: "GCCs",
            description: "Strategic staffing support for Global Capability Center workforce scaling and offshore expansion."
        }
    ],

    // Outcomes
    outcomes_title: "Recruitment Services Built Around Measurable Hiring Outcomes",
    outcomes_list: [
        {
            title: "Faster Hiring Cycles",
            description: "Reduce recruitment turnaround through optimized sourcing and structured hiring workflows."
        },
        {
            title: "Better Workforce Quality",
            description: "Access technically evaluated and operationally aligned professionals faster."
        },
        {
            title: "Workforce Scalability",
            description: "Support rapid expansion without compromising recruitment efficiency."
        },
        {
            title: "Reduced Hiring Costs",
            description: "Improve hiring productivity through structured recruitment operations."
        },
        {
            title: "Better Candidate Experience",
            description: "Streamlined communication and smoother recruitment journeys."
        },
        {
            title: "Long-Term Workforce Stability",
            description: "Build stronger teams aligned with operational and business goals."
        }
    ],

    // Final CTA
    final_cta_title: "Build Future-Ready Teams Through Specialized Recruitment Strategies",
    final_cta_description: "Partner with a highly experienced Specialized Recruitment Services company delivering executive hiring, startup recruitment, GCC workforce expansion, offshore hiring, remote recruitment, and AI-powered talent acquisition solutions for modern businesses.",
    final_cta_primary: "Book Specialized Hiring Strategy Session",
    final_cta_secondary: "Request Custom Recruitment Plan"
};

// Static content for Recruitment Process Outsourcing (RPO) Services
const rpoStaticData = {
    title: "Recruitment Process Outsourcing (RPO) Services",
    slug: "rpo",
    meta_title: "Recruitment Process Outsourcing (RPO) Services | Merida HR Consulting",
    meta_description: "Streamline your hiring process with Merida's Recruitment Process Outsourcing (RPO) services, project RPO, and end-to-end recruitment management.",

    // Hero Section
    hero_title: "Recruitment Process Outsourcing (RPO) Services",
    hero_subtitle: "Transform Hiring Operations Into Scalable Recruitment Systems",
    hero_description: "Helping businesses streamline sourcing operations, screening workflows, interview coordination, onboarding support, recruitment reporting, and workforce planning through highly structured recruitment outsourcing frameworks built for long-term business scalability.",
    hero_primary_cta: "Book RPO Strategy Consultation",
    hero_secondary_cta: "Explore RPO Solutions",
    hero_image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=800&q=80",

    // Enquiry Form Metadata
    enquiry_title: "Speak With Recruitment Process Outsourcing Specialists",
    enquiry_description: "Every business operates with different hiring structures, recruitment challenges, workforce scaling priorities, and operational hiring requirements. Whether organizations require end-to-end recruitment outsourcing, project-based hiring support, enterprise recruitment management, startup workforce scaling, GCC hiring support, or high-volume talent acquisition systems, our RPO specialists design customized recruitment frameworks aligned with workforce objectives and operational growth plans. Discuss your requirements with our recruitment specialists today.",
    enquiry_cta: "Request RPO Consultation",
    enquiry_fields: [
        "Full Name",
        "Company Name",
        "Business Email",
        "Phone Number",
        "Industry Type",
        "Hiring Requirement",
        "Current Hiring Volume",
        "Business Location",
        "Hiring Timeline",
        "Message"
    ],

    // Categories
    categories_title: "Explore Our RPO Services",
    categories_list: [
        {
            title: "Recruitment Process Outsourcing Services",
            micro_heading: "End-To-End Recruitment Management Services Built For Workforce Scalability",
            description: <>"Modern businesses often struggle with fragmented recruitment operations, inconsistent hiring execution, sourcing delays, interview coordination inefficiencies, and increasing internal HR dependency. Our Recruitment Process Outsourcing Services help organizations build structured recruitment ecosystems capable of managing workforce acquisition more efficiently through scalable hiring frameworks, AI-powered sourcing systems, recruitment workflow optimization, and operational hiring management. Learn how <a href="/blog/recruitment-process-outsourcing-guide">Recruitment Process Outsourcing (RPO)</a> helps businesses reduce hiring costs while improving recruitment scalability."</>,
            points: [
                "Improve recruitment consistency",
                "Reduce hiring bottlenecks",
                "Strengthen workforce quality",
                "Accelerate candidate sourcing",
                "Improve recruitment visibility",
                "Optimize hiring workflows",
                "Reduce operational recruitment delays",
                "Improve workforce scalability"
            ],
            cta_text: "Explore Recruitment Process Outsourcing Services"
        },
        {
            title: "Project RPO Services",
            micro_heading: "Scalable Project-Based Recruitment Solutions For Rapid Workforce Expansion",
            description: "Business expansion initiatives, technology transformation projects, GCC workforce scaling, operational ramp-ups, and high-volume recruitment drives often create temporary hiring demands that internal teams struggle to manage efficiently. Our Project RPO Services are designed to help organizations rapidly scale recruitment operations through specialized project-based hiring frameworks built for workforce agility and accelerated talent deployment.",
            points: [
                "Scale workforce operations rapidly",
                "Improve recruitment coordination",
                "Accelerate hiring timelines",
                "Manage high-volume recruitment efficiently",
                "Reduce sourcing delays",
                "Support business expansion projects",
                "Improve workforce deployment speed",
                "Optimize recruitment scalability"
            ],
            cta_text: "Explore Project RPO Services"
        }
    ],

    // Differentiators
    differentiators_title: "Why Choose Merida HR Consulting For Recruitment Process Outsourcing (RPO) Services?",
    differentiators_list: [
        {
            title: "AI-Powered Recruitment Operations Infrastructure",
            description: <>We leverage AI-supported sourcing intelligence,<a href="/blog/recruitment-automation">recruitment automation systems</a>, structured hiring workflows, workforce analytics, and recruitment management technologies to improve hiring scalability, sourcing precision, workforce visibility, and recruitment efficiency across every stage of hiring execution.</>
        },
        {
            title: "End-To-End Recruitment Process Management",
            description: "Unlike traditional staffing agencies that focus only on candidate delivery, our Recruitment Process Outsourcing (RPO) Services manage complete recruitment operations including sourcing, screening, workflow coordination, reporting, onboarding support, process optimization, and workforce hiring management."
        },
        {
            title: "Scalable Workforce Expansion Support",
            description: "Whether businesses are scaling startup operations, expanding enterprise teams, launching GCCs, managing project hiring, or building high-volume workforce systems, our RPO infrastructure is built to support sustainable recruitment scalability without operational inefficiencies."
        },
        {
            title: "Reduced Internal HR Dependency",
            description: <>Our structured RPO frameworks reduce administrative recruitment burden on internal HR teams by managing sourcing operations, interview scheduling, hiring coordination,Strong candidate communication also strengthens <a href="https://universumglobal.com/resources/what-is-employer-branding-definition/">employer branding</a>, improving the overall recruitment experience.</>
        }
    ],

    // Process Framework
    process_title: "Our Recruitment Process Outsourcing Execution Process",
    process_list: [
        {
            title: "Workforce & Recruitment Discovery",
            description: "Understanding hiring structures, recruitment gaps, workforce goals, operational hiring challenges, and recruitment scalability requirements."
        },
        {
            title: "Recruitment Process Assessment",
            description: "Evaluating current recruitment workflows, sourcing systems, operational inefficiencies, hiring bottlenecks, and workforce acquisition gaps."
        },
        {
            title: "RPO Strategy Architecture",
            description: "Designing customized recruitment outsourcing frameworks aligned with workforce scalability, operational hiring goals, and business growth priorities."
        },
        {
            title: "AI-Powered Talent Sourcing",
            description: "Leveraging sourcing intelligence systems, recruitment technologies, hiring channels, and workforce analytics for structured candidate acquisition."
        },
        {
            title: "Candidate Screening & Evaluation",
            description: "Managing structured screening workflows focused on technical relevance, workforce compatibility, operational alignment, and hiring quality."
        },
        {
            title: "Interview & Hiring Coordination",
            description: "Managing interview scheduling, stakeholder communication, recruitment tracking, hiring workflows, and workforce coordination processes."
        },
        {
            title: "Onboarding & Workforce Transition Support",
            description: "Supporting onboarding coordination, documentation workflows, workforce transition management, and recruitment completion processes."
        },
        {
            title: "Recruitment Performance Optimization",
            description: <>Continuously improving recruitment workflows through measurable <a href="https://www.zoho.com/recruit/recruitment-kpis.html">recruitment KPIs and metrics</a> helps optimize hiring performance.</>
        }
    ],

    // Industry Expertise
    industry_title: "Industry-Focused Staffing & Recruitment Expertise",
    industry_description: "Different industries require different hiring models, workforce strategies, recruitment timelines, and sourcing ecosystems. Our recruitment specialists understand industry-specific workforce demands deeply — enabling businesses to access more relevant talent pools and stronger hiring outcomes.",
    industry_list: [
        {
            title: "IT & Software",
            description: "Technology recruitment across engineering, AI, cybersecurity, cloud, DevOps, ERP, and enterprise systems."
        },
        {
            title: "Healthcare",
            description: "Healthcare workforce solutions for hospitals, diagnostics, healthcare operations, and medical administration."
        },
        {
            title: "Manufacturing",
            description: "Industrial workforce recruitment, operational staffing, plant hiring, and production workforce deployment."
        },
        {
            title: "BFSI",
            description: "Banking, insurance, finance operations, fintech, and compliance workforce hiring support."
        },
        {
            title: "Ecommerce & Retail",
            description: "Scalable workforce hiring for ecommerce operations, warehousing, retail support, and customer experience teams."
        },
        {
            title: "Logistics & Supply Chain",
            description: "Operational staffing solutions for logistics ecosystems and supply chain workforce management."
        },
        {
            title: "Startups",
            description: "Agile recruitment systems designed for rapidly growing startup environments."
        },
        {
            title: "GCCs",
            description: "Strategic staffing support for Global Capability Center workforce scaling and offshore expansion."
        }
    ],

    // Outcomes
    outcomes_title: "Recruitment Services Built Around Measurable Hiring Outcomes",
    outcomes_list: [
        {
            title: "Faster Hiring Cycles",
            description: "Reduce recruitment turnaround through optimized sourcing and structured hiring workflows."
        },
        {
            title: "Better Workforce Quality",
            description: "Access technically evaluated and operationally aligned professionals faster."
        },
        {
            title: "Workforce Scalability",
            description: "Support rapid expansion without compromising recruitment efficiency."
        },
        {
            title: "Reduced Hiring Costs",
            description: "Improve hiring productivity through structured recruitment operations."
        },
        {
            title: "Better Candidate Experience",
            description: "Streamlined communication and smoother recruitment journeys."
        },
        {
            title: "Long-Term Workforce Stability",
            description: "Build stronger teams aligned with operational and business goals."
        }
    ],

    // Final CTA
    final_cta_title: "Build Smarter Recruitment Systems Through Strategic RPO Solutions",
    final_cta_description: "Partner with a highly experienced Recruitment Process Outsourcing (RPO) Services company delivering scalable hiring infrastructure, project RPO solutions, AI-powered recruitment systems, workforce process optimization, and end-to-end talent acquisition support for modern businesses.",
    final_cta_primary: "Book RPO Strategy Session",
    final_cta_secondary: "Request Custom RPO Plan"
};

// Static content for HR Outsourcing Services
const hrOutsourcingStaticData = {
    title: "HR Outsourcing Services",
    slug: "hr-outsourcing",
    meta_title: "HR Outsourcing Services | Merida HR Consulting",
    meta_description: "Simplify workforce operations with Merida's HR outsourcing services, employee administration support, compliance coordination, and documentation management.",

    // Hero Section
    hero_title: "HR Outsourcing Services",
    hero_subtitle: "Simplify Workforce Operations Through Structured HR Management Systems",
    hero_description: <>Designed to help businesses improve workforce administration, simplify HR operations, strengthen compliance management, and optimize employee management processes through scalable HR support systems. Learn how <a href="/blog/hr-outsourcing-services-guide">HR outsourcing services</a> help organizations reduce administrative overhead while improving operational efficiency.</>,
    hero_primary_cta: "Book HR Outsourcing Consultation",
    hero_secondary_cta: "Explore HR Outsourcing Solutions",
    hero_image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=800&q=80",

    // Enquiry Form Metadata
    enquiry_title: "Speak With HR Outsourcing Specialists",
    enquiry_description: "Every organization operates with different workforce structures, employee management systems, HR process challenges, and compliance requirements. Whether businesses require HR administration support, employee documentation management, workforce compliance assistance, HR process outsourcing, or structured operational HR systems, our HR specialists create customized outsourcing frameworks aligned with organizational goals and workforce management priorities. Discuss your requirements with our HR outsourcing specialists today.",
    enquiry_cta: "Request HR Outsourcing Consultation",
    enquiry_fields: [
        "Full Name",
        "Company Name",
        "Business Email",
        "Phone Number",
        "Industry Type",
        "HR Requirement",
        "Workforce Size",
        "Business Location",
        "Support Requirement",
        "Message"
    ],

    // Categories
    categories_title: "Explore Our HR Outsourcing Services",
    categories_list: [
        {
            title: "HR Administration Services",
            micro_heading: "Structured HR Operations Support Designed For Workforce Efficiency",
            description: <>Managing day-to-day HR operations internally often creates administrative inefficiencies, inconsistent employee management workflows, operational delays, and increased HR workload. Our HR Administration Services help businesses streamline operational HR activities through structured workforce management systems designed to improve employee administration, process efficiency, and organizational coordination.Explore the benefits of <a href="https://www.bamboohr.com/blog/guide-hr-best-practices">HR administration best practices</a> for improving workforce efficiency.</>,
            points: [
                "Improve operational HR efficiency",
                "Reduce administrative workload",
                "Strengthen workforce coordination",
                "Standardize HR processes",
                "Improve employee management workflows",
                "Support structured workforce operations"
            ],
            cta_text: "Explore HR Administration Services"
        },
        {
            title: "HR Documentation Services",
            micro_heading: "Organized Employee Documentation Systems For Structured Workforce Management",
            description: "Unstructured employee documentation processes often create operational confusion, compliance risks, inconsistent employee records, and workforce management inefficiencies. Our HR Documentation Services help organizations maintain organized employee records and structured HR documentation systems through process-driven workforce administration frameworks.",
            points: [
                "Maintain accurate employee records",
                "Improve documentation accessibility",
                "Reduce HR documentation errors",
                "Strengthen workforce process consistency",
                "Support structured employee lifecycle management",
                "Improve operational transparency"
            ],
            cta_text: "Explore HR Documentation Services"
        },
        {
            title: "HR Compliance Services",
            micro_heading: "Workforce Compliance Solutions Built For Operational Stability",
            description: <>Workforce compliance management requires structured monitoring systems, policy alignment, documentation accuracy, and operational consistency to help businesses reduce compliance risks and maintain organized workforce practices. Our HR Compliance Services help organizations strengthen compliance management frameworks through operational HR systems designed to support workforce governance and process standardization.Discover the importance of <a href="/blog/hr-compliance-india">HR compliance in India</a> for maintaining legally compliant workforce operations.</>,
            points: [
                "Improve workforce compliance management",
                "Reduce operational compliance risks",
                "Strengthen HR process consistency",
                "Maintain structured workforce records",
                "Improve policy implementation workflows",
                "Support labor compliance readiness"
            ],
            cta_text: "Explore HR Compliance Services"
        }
    ],

    // Differentiators
    differentiators_title: "Why Choose Merida HR Consulting For HR Outsourcing Services?",
    differentiators_list: [
        {
            title: "Structured HR Operations Management",
            description: "We help businesses streamline fragmented HR processes through organized workforce administration systems, operational HR frameworks, structured documentation workflows, and scalable employee management solutions."
        },
        {
            title: "Workforce Compliance Support",
            description: "Our HR specialists help organizations strengthen compliance readiness through structured employee documentation systems, operational workforce policies, process standardization, and HR compliance management frameworks."
        },
        {
            title: "Reduced Administrative Workload",
            description: "Our HR Outsourcing Services reduce internal HR dependency by managing administrative workforce operations, employee coordination workflows, HR records management, and operational process support more efficiently."
        },
        {
            title: "Scalable HR Support Systems",
            description: "Whether businesses are scaling operations, expanding teams, opening new departments, or managing multi-location workforce structures, our HR systems are built to support long-term operational scalability."
        },
        {
            title: "Process-Driven Workforce Management",
            description: "We combine operational HR expertise, workforce administration systems, documentation management workflows, and compliance coordination frameworks to help organizations maintain organized and efficient workforce operations."
        }
    ],

    // Process Framework
    process_title: "Our HR Outsourcing Execution Process",
    process_list: [
        {
            title: "HR Process Discovery",
            description: "Understanding workforce structures, operational HR challenges, employee management workflows, and organizational support requirements."
        },
        {
            title: "HR Operations Assessment",
            description: "Evaluating current HR administration systems, documentation processes, compliance workflows, and workforce coordination gaps."
        },
        {
            title: "HR Outsourcing Strategy Planning",
            description: "Designing customized HR outsourcing frameworks aligned with workforce operations and organizational management priorities."
        },
        {
            title: "HR Administration & Documentation Management",
            description: "Implementing structured workforce administration systems and employee documentation workflows."
        },
        {
            title: "Compliance Coordination & Workforce Support",
            description: "Managing operational HR coordination, compliance support systems, employee records workflows, and policy management structures."
        },
        {
            title: "Operational HR Optimization",
            description: <>Continuously improve HR processes through <a href="https://www.aihr.com/blog/hr-process-improvement/"> HR process optimization </a>strategies that enhance workforce productivity and operational efficiency.</>
        }
    ],

    // Industry Expertise
    industry_title: "Industry-Focused Staffing & Recruitment Expertise",
    industry_description: "Different industries require different hiring models, workforce strategies, recruitment timelines, and sourcing ecosystems. Our recruitment specialists understand industry-specific workforce demands deeply — enabling businesses to access more relevant talent pools and stronger hiring outcomes.",
    industry_list: [
        {
            title: "IT & Software",
            description: "Technology recruitment across engineering, AI, cybersecurity, cloud, DevOps, ERP, and enterprise systems."
        },
        {
            title: "Healthcare",
            description: "Healthcare workforce solutions for hospitals, diagnostics, healthcare operations, and medical administration."
        },
        {
            title: "Manufacturing",
            description: "Industrial workforce recruitment, operational staffing, plant hiring, and production workforce deployment."
        },
        {
            title: "BFSI",
            description: "Banking, insurance, finance operations, fintech, and compliance workforce hiring support."
        },
        {
            title: "Ecommerce & Retail",
            description: "Scalable workforce hiring for ecommerce operations, warehousing, retail support, and customer experience teams."
        },
        {
            title: "Logistics & Supply Chain",
            description: "Operational staffing solutions for logistics ecosystems and supply chain workforce management."
        },
        {
            title: "Startups",
            description: "Agile recruitment systems designed for rapidly growing startup environments."
        },
        {
            title: "GCCs",
            description: "Strategic staffing support for Global Capability Center workforce scaling and offshore expansion."
        }
    ],

    // Outcomes
    outcomes_title: "Recruitment Services Built Around Measurable Hiring Outcomes",
    outcomes_list: [
        {
            title: "Faster Hiring Cycles",
            description: "Reduce recruitment turnaround through optimized sourcing and structured hiring workflows."
        },
        {
            title: "Better Workforce Quality",
            description: "Access technically evaluated and operationally aligned professionals faster."
        },
        {
            title: "Workforce Scalability",
            description: "Support rapid expansion without compromising recruitment efficiency."
        },
        {
            title: "Reduced Hiring Costs",
            description: "Improve hiring productivity through structured recruitment operations."
        },
        {
            title: "Better Candidate Experience",
            description: "Streamlined communication and smoother recruitment journeys."
        },
        {
            title: "Long-Term Workforce Stability",
            description: "Build stronger teams aligned with operational and business goals."
        }
    ],

    // Final CTA
    final_cta_title: "Build Structured & Scalable Workforce Operations Through Strategic HR Outsourcing",
    final_cta_description: "Partner with a highly experienced HR Outsourcing Services company delivering workforce administration support, employee documentation management, HR compliance systems, operational HR process optimization, and scalable workforce management solutions for modern businesses.",
    final_cta_primary: "Book HR Outsourcing Strategy Session",
    final_cta_secondary: "Request Custom HR Outsourcing Plan"
};

// Static content for Payroll & Compliance Services
const payrollStaticData = {
    title: "Payroll & Compliance Services",
    slug: "payroll-compliance",
    meta_title: "Payroll & Compliance Services | Merida HR Consulting",
    meta_description: "Streamline payroll operations, strengthen workforce transparency, improve payroll accuracy, and simplify employee compensation management with Merida's payroll outsourcing solutions.",

    // Hero Section
    hero_title: "Payroll & Compliance Services",
    hero_subtitle: "Manage Payroll Operations Accurately & Efficiently Through Scalable Outsourcing Solutions",
    hero_description: <>Managing payroll operations internally often creates administrative complexity, payroll inconsistencies, compliance challenges, salary processing delays, workforce coordination issues, and operational inefficiencies. Streamline payroll operations, strengthen workforce transparency, improve payroll accuracy, simplify employee compensation management, and maintain organized payroll administration systems through scalable payroll outsourcing solutions. Learn how <a href="/blog/payroll-outsourcing-services-guide">payroll outsourcing services</a> help businesses reduce administrative costs while improving payroll accuracy.</>,
    hero_primary_cta: "Book Payroll Consultation",
    hero_secondary_cta: "Explore Payroll & Compliance Solutions",
    hero_image: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&w=800&q=80",

    // Enquiry Form Metadata
    enquiry_title: "Speak With Payroll & Compliance Specialists",
    enquiry_description: "Every organization operates with different payroll structures, workforce models, salary processing systems, contractor management requirements, and compliance responsibilities. Whether businesses require payroll outsourcing support, employee salary processing, contractor payroll administration, payroll compliance coordination, or scalable payroll management systems, our payroll specialists create customized payroll frameworks aligned with operational goals and workforce management priorities. Discuss your requirements with our payroll & compliance specialists today.",
    enquiry_cta: "Request Payroll Consultation",
    enquiry_fields: [
        "Full Name",
        "Company Name",
        "Business Email",
        "Phone Number",
        "Industry Type",
        "Payroll Requirement",
        "Workforce Size",
        "Business Location",
        "Support Requirement",
        "Message"
    ],

    // Categories
    categories_title: "Explore Our Payroll & Compliance Services",
    categories_list: [
        {
            title: "Payroll Services",
            micro_heading: "Structured Payroll Management Solutions Designed For Workforce Accuracy",
            description: "Managing payroll operations manually often creates salary inconsistencies, payroll processing delays, workforce transparency issues, and operational inefficiencies that impact employee experience and business productivity. Our Payroll Services help organizations streamline workforce payroll operations through structured payroll management systems designed to improve salary accuracy, workforce coordination, payroll administration, and operational efficiency.",
            points: [
                "Improve payroll accuracy",
                "Reduce payroll administration workload",
                "Strengthen workforce payroll transparency",
                "Simplify employee compensation management",
                "Improve payroll coordination workflows",
                "Support structured payroll operations"
            ],
            cta_text: "Explore Payroll Services"
        },
        {
            title: "Payroll Processing Services",
            micro_heading: "Accurate Salary Processing Systems For Efficient Workforce Management",
            description: <>Payroll processing requires structured operational systems capable of managing salary calculations, payroll records, employee compensation workflows, and payroll coordination accurately across workforce categories. Our Payroll Processing Services help businesses maintain organized salary processing operations through scalable payroll systems designed for operational consistency and workforce efficiency.Explore <a href="/blog/payroll-processing-best-practices">payroll processing best practices</a> for efficient salary management.</>,
            points: [
                "Ensure timely salary processing",
                "Reduce payroll processing errors",
                "Improve workforce payment coordination",
                "Strengthen payroll workflow consistency",
                "Support accurate employee compensation management",
                "Improve payroll operational efficiency"
            ],
            cta_text: "Explore Payroll Processing Services"
        },
        {
            title: "Payroll Outsourcing Services",
            micro_heading: "Scalable Payroll Outsourcing Solutions Built For Operational Efficiency",
            description: "Internal payroll management often increases administrative workload, operational complexity, payroll dependency risks, and workforce coordination challenges. Our Payroll Outsourcing Services help organizations streamline payroll administration through structured outsourcing frameworks designed to improve payroll efficiency, workforce coordination, compliance support, and operational scalability.",
            points: [
                "Reduce internal payroll workload",
                "Improve payroll process efficiency",
                "Strengthen workforce payroll management",
                "Simplify payroll administration workflows",
                "Improve operational payroll coordination",
                "Support scalable payroll operations"
            ],
            cta_text: "Explore Payroll Outsourcing Services"
        },
        {
            title: "Contractor Payroll Management Services",
            micro_heading: "Specialized Payroll Solutions For Contract & Project-Based Workforce Management",
            description: "Managing contractor payroll operations requires structured coordination systems capable of handling workforce payments, project payroll workflows, operational transparency, and contractor compensation management efficiently. Our Contractor Payroll Management Services help businesses maintain organized contractor payroll operations through scalable workforce payroll systems designed for flexibility and payroll accuracy.",
            points: [
                "Simplify contractor payroll administration",
                "Improve workforce payment accuracy",
                "Strengthen payroll coordination workflows",
                "Reduce contractor payroll processing delays",
                "Support project-based workforce management",
                "Improve payroll operational transparency"
            ],
            cta_text: "Explore Contractor Payroll Management Services"
        }
    ],

    // Differentiators
    differentiators_title: "Why Choose Merida HR Consulting For Payroll & Compliance Services?",
    differentiators_list: [
        {
            title: "Structured Payroll Operations Management",
            description: "We help businesses streamline fragmented payroll workflows through organized payroll systems, workforce compensation management frameworks, operational payroll coordination, and scalable payroll administration solutions."
        },
        {
            title: "Workforce Compliance Support",
            description: <>Our payroll specialists help organizations strengthen workforce compliance readiness through structured payroll processes and<a href="https://in.adp.com/resources/insights/payroll-compliance.aspx">payroll compliance management strategies</a>that reduce legal and financial risks.</>,

        },
        {
            title: "Reduced Payroll Administration Workload",
            description: "Our Payroll & Compliance Services reduce internal administrative dependency by managing payroll operations, employee compensation coordination, contractor payroll workflows, and payroll administration processes more efficiently."
        },
        {
            title: "Scalable Payroll Support Systems",
            description: "Whether businesses are scaling operations, managing multi-location workforce structures, expanding contractor teams, or supporting workforce growth, our payroll systems are designed to support long-term operational scalability."
        },
        {
            title: "Process-Driven Payroll Management",
            description: "We combine payroll expertise, structured payroll systems, workforce coordination workflows, compensation management frameworks, and operational payroll support to help organizations maintain accurate and efficient payroll operations."
        }
    ],

    // Process Framework
    process_title: "Our Payroll & Compliance Execution Process",
    process_list: [
        {
            title: "Payroll Process Discovery",
            description: "Understanding workforce payroll structures, salary processing workflows, operational payroll challenges, and business support requirements."
        },
        {
            title: "Payroll Operations Assessment",
            description: "Evaluating existing payroll systems, compensation workflows, compliance coordination processes, and workforce payroll gaps."
        },
        {
            title: "Payroll Strategy Planning",
            description: "Designing customized payroll outsourcing frameworks aligned with workforce operations and payroll management priorities."
        },
        {
            title: "Payroll Administration & Salary Processing",
            description: <>Implementing structured payroll workflows supported by <a href="https://in.adp.com/resources/insights/payroll-compliance.aspx" >payroll automation</a> improves efficiency and minimizes manual errors.</>
        },
        {
            title: "Compliance Coordination & Workforce Payroll Support",
            description: "Managing payroll coordination systems, operational compliance workflows, contractor payroll administration, and workforce compensation support."
        },
        {
            title: "Payroll Operations Optimization",
            description: "Continuously improving payroll workflows, salary processing systems, workforce coordination operations, and payroll management efficiency."
        }
    ],

    // Industry Expertise
    industry_title: "Industry-Focused Payroll & Compliance Expertise",
    industry_description: "Different industries operate with different payroll models, workforce compliance regulations, contractor structures, and salary processing timelines. Our payroll specialists understand industry-specific payroll demands deeply — enabling businesses to maintain organized, accurate, and fully compliant payroll administration workflows.",
    industry_list: [
        {
            title: "IT & Software",
            description: "Payroll administration for technology teams, remote developers, contractor agreements, and global compensation compliance."
        },
        {
            title: "Healthcare",
            description: "Healthcare payroll processing support, hospital shift calculations, multi-location staff compensation, and regulatory filings."
        },
        {
            title: "Manufacturing",
            description: "Industrial shift payroll, overtime processing, plant workers ESI & PF compliance, and operational staff payroll."
        },
        {
            title: "BFSI",
            description: "Highly compliant banking and financial payroll processing, security audits, and financial regulation alignment."
        },
        {
            title: "Ecommerce & Retail",
            description: "Scalable retail staff payroll, high-turnover temporary workforce payments, and commission-based structures."
        },
        {
            title: "Logistics & Supply Chain",
            description: "Operational driver and warehouse staff payroll, shift allowances, and statutory compliance management."
        },
        {
            title: "Startups",
            description: "Agile, automated payroll systems designed for fast-growing startup teams and stock option records."
        },
        {
            title: "GCCs",
            description: "Strategic payroll support for Global Capability Centers, cross-border payments, and offshore team administration."
        }
    ],

    // Outcomes
    outcomes_title: "Payroll Services Built Around Measurable Operational Outcomes",
    outcomes_list: [
        {
            title: "100% Payroll Accuracy",
            description: "Eliminate manual errors, salary calculation discrepancies, and payroll inconsistencies."
        },
        {
            title: "Zero Compliance Risks",
            description: "Maintain absolute adherence to PF, ESI, professional tax, and statutory payroll regulations."
        },
        {
            title: "Reduced Administrative Cost",
            description: "Cut down internal payroll processing expenses and administrative overhead."
        },
        {
            title: "Timely Salary Processing",
            description: "Ensure consistent and prompt salary payments to build stronger workforce trust."
        },
        {
            title: "Streamlined Contractor Payments",
            description: "Manage freelance, contractor, and project-based employee payroll seamlessly."
        },
        {
            title: "Better Process Transparency",
            description: "Provide clear, accessible, and structured payroll documentation and reporting."
        }
    ],

    // Final CTA
    final_cta_title: "Build Accurate & Scalable Payroll Operations Through Strategic Payroll Management Solutions",
    final_cta_description: "Partner with an experienced Payroll & Compliance Services company delivering payroll outsourcing support, salary processing management, contractor payroll administration, workforce compliance coordination, and scalable payroll management solutions for modern businesses.",
    final_cta_primary: "Book Payroll Strategy Session",
    final_cta_secondary: "Request Custom Payroll Management Plan"
};

// Static content for Statutory & Compliance Management Services
const statutoryStaticData = {
    title: "Statutory & Compliance Management Services",
    slug: "statutory-compliance",
    meta_title: "Statutory & Compliance Management Services | Merida HR Consulting",
    meta_description: "Strengthen workforce compliance, reduce legal risks, manage PF & ESI, and support labor law adherence through Merida's statutory compliance management solutions.",

    // Hero Section
    hero_title: "Statutory & Compliance Management Services",
    hero_subtitle: "Strengthen Workforce Compliance Through Structured Regulatory Management Systems",
    hero_description: "Managing workforce compliance internally requires structured tracking, labor law coordination, and statutory documentation management. We help organizations streamline PF & ESI handling, employee records management, compliance reporting, and labor law filings through scalable regulatory support systems built for operational stability.",
    hero_primary_cta: "Book Compliance Consultation",
    hero_secondary_cta: "Explore Statutory & Compliance Services",
    hero_image: "https://images.unsplash.com/photo-1450133064473-71024230f91b?auto=format&fit=crop&w=800&q=80",

    // Enquiry Form Metadata
    enquiry_title: "Speak With Statutory & Compliance Specialists",
    enquiry_description: "Every organization operates with different workforce structures, employee compliance requirements, labor law responsibilities, and statutory management challenges. Whether businesses require PF & ESI management support, labor law compliance assistance, workforce compliance coordination, employee statutory documentation management, or structured compliance administration systems, our compliance specialists create customized frameworks aligned with operational goals and workforce management priorities. Discuss your requirements with our statutory & compliance specialists today.",
    enquiry_cta: "Request Compliance Consultation",
    enquiry_fields: [
        "Full Name",
        "Company Name",
        "Business Email",
        "Phone Number",
        "Industry Type",
        "Compliance Requirement",
        "Workforce Size",
        "Business Location",
        "Support Requirement",
        "Message"
    ],

    // Categories
    categories_title: "Explore Our Statutory & Compliance Management Services",
    categories_list: [
        {
            title: "Statutory Compliance Services",
            micro_heading: "Structured Workforce Compliance Solutions Designed For Regulatory Stability",
            description: <>Managing workforce compliance without structured systems often creates legal risks, operational inconsistencies, compliance gaps, workforce documentation issues, and administrative inefficiencies. Our Statutory Compliance Services help organizations strengthen compliance management through scalable workforce compliance systems designed to support regulatory adherence, operational transparency, and structured employee administration.Learn how <a href="/blog/statutory-compliance-services-guide">statutory compliance services</a> help businesses reduce legal risks while ensuring long-term regulatory compliance.</>,
            points: [
                "Improve workforce compliance management",
                "Reduce compliance-related risks",
                "Strengthen workforce documentation systems",
                "Improve operational compliance transparency",
                "Support labor law compliance readiness",
                "Maintain structured compliance operations"
            ],
            cta_text: "Explore Statutory Compliance Services"
        },
        {
            title: "PF & ESI Management Services",
            micro_heading: "Reliable PF & ESI Administration Solutions For Organized Workforce Operations",
            description: <>Managing PF & ESI operations requires structured coordination systems capable of handling employee records, statutory workflows, compliance tracking, and workforce contribution management accurately and efficiently. Our PF & ESI Management Services help businesses streamline workforce statutory operations through organized compliance frameworks designed to improve operational consistency and employee compliance administration.Learn how <a href="/blog/pf-esi-management-guide    " >PF & ESI management</a> helps organizations remain compliant with employee benefit regulations.</>,
            points: [
                "Simplify PF & ESI coordination",
                "Improve workforce contribution management",
                "Reduce compliance processing delays",
                "Strengthen employee statutory documentation workflows",
                "Support structured workforce compliance systems",
                "Improve operational compliance efficiency"
            ],
            cta_text: "Explore PF & ESI Management Services"
        }
    ],

    // Differentiators
    differentiators_title: "Why Choose Merida HR Consulting For Statutory & Compliance Management Services?",
    differentiators_list: [
        {
            title: "Structured Compliance Operations Management",
            description: "We help businesses streamline fragmented compliance workflows through organized statutory systems, workforce documentation frameworks, operational compliance coordination, and scalable workforce management solutions."
        },
        {
            title: "Workforce Regulatory Compliance Support",
            description: <>Our compliance specialists help organizations strengthen workforce compliance readiness through structured labor law coordination, employee statutory documentation systems, PF & ESI management workflows, and operational compliance frameworks.Implementing structured <a href="https://www.greythr.com/guides/complete-guide-statutory/" >employee statutory documentation</a> improves audit readiness and workforce compliance.</>
        },
        {
            title: "Reduced Administrative Compliance Burden",
            description: "Our Statutory & Compliance Management Services reduce internal administrative dependency by managing workforce compliance coordination, statutory documentation workflows, labor compliance operations, and employee compliance management processes more efficiently."
        },
        {
            title: "Scalable Compliance Support Systems",
            description: "Whether businesses are scaling workforce operations, managing multi-location teams, expanding departments, or Effective workforce governance improves policy implementation, compliance, and organizational accountability, our compliance systems are designed to support long-term operational scalability."
        },
        {
            title: "Process-Driven Compliance Management",
            description: "We combine compliance expertise, workforce coordination systems, operational compliance workflows, documentation management frameworks, and structured statutory support to help organizations maintain compliant and operationally stable workforce environments."
        }
    ],

    // Process Framework
    process_title: "Our Statutory & Compliance Management Execution Process",
    process_list: [
        {
            title: "Compliance Process Discovery",
            description: "Understanding workforce compliance structures, labor law responsibilities, operational challenges, and statutory management requirements."
        },
        {
            title: "Compliance Operations Assessment",
            description: "Evaluating current compliance systems, workforce documentation workflows, statutory coordination processes, and operational compliance gaps."
        },
        {
            title: "Compliance Strategy Planning",
            description: <>Designing customized <a href="https://www.linkedin.com/pulse/statutory-compliance-checklist-2026-practical-guide-indian-fabdc/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">statutory compliance frameworks</a> aligned with workforce operations and organizational compliance priorities.</>
        },
        {
            title: "Workforce Compliance & Documentation Management",
            description: "Implementing structured compliance workflows and employee statutory documentation systems."
        },
        {
            title: "PF, ESI & Regulatory Coordination",
            description: "Managing workforce compliance coordination systems, PF & ESI workflows, operational statutory processes, and employee compliance administration."
        },
        {
            title: "Compliance Operations Optimization",
            description: "Continuously improving workforce compliance systems, operational coordination workflows, statutory management processes, and compliance efficiency."
        }
    ],

    // Industry Expertise
    industry_title: "Industry-Focused Compliance Expertise",
    industry_description: "Different industries operate under different regulatory frameworks, labor law complexities, and statutory guidelines. Our compliance specialists possess deep domain expertise to help your business stay compliant, audit-ready, and operationally stable.",
    industry_list: [
        {
            title: "IT & Software",
            description: "Statutory compliance support for software firms, virtual office setups, state-specific labor codes, and remote compliance."
        },
        {
            title: "Healthcare",
            description: "Medical staff labor compliance, shift regulation monitoring, statutory filings, and healthcare industry safety guidelines."
        },
        {
            title: "Manufacturing",
            description: "Factories Act compliance, factory licensing, labor audits, contract labor regulation, PF & ESI handling, and safety audits."
        },
        {
            title: "BFSI",
            description: "Statutory guidelines adherence, corporate governance support, wage codes compliance, and employee records maintenance."
        },
        {
            title: "Ecommerce & Retail",
            description: "Shops & Establishment Act compliance, commercial compliance records, commission wage systems, and temporary staff PF & ESI."
        },
        {
            title: "Logistics & Supply Chain",
            description: "Compliance for transport workers, interstate transport statutory filings, warehouse workers PF & ESI, and shift compliance."
        },
        {
            title: "Startups",
            description: "Early-stage statutory setup, basic labor registrations, PF/ESI registration, and regulatory guidance for scaling teams."
        },
        {
            title: "GCCs",
            description: "Global Capability Center labor filings, double taxation advisory support, statutory filings, and local compliance systems."
        }
    ],

    // Outcomes
    outcomes_title: "Statutory Services Built Around Measurable Regulatory Outcomes",
    outcomes_list: [
        {
            title: "100% Legal Adherence",
            description: "Ensure complete alignment with national and state-specific labor laws and guidelines."
        },
        {
            title: "Zero Penalty History",
            description: "Reduce risk of legal compliance delays, regulatory warnings, or administrative penalties."
        },
        {
            title: "Streamlined PF & ESI",
            description: "Accurate monthly PF & ESI contributions calculations, deposits, and documentation."
        },
        {
            title: "Continuous Audit Readiness",
            description: "Maintain organized, well-documented statutory records to pass labor audits smoothly."
        },
        {
            title: "Reduced In-House Workload",
            description: "Free internal HR team from tracking complex and dynamic government labor regulations."
        },
        {
            title: "Transparent Compliance Reporting",
            description: "Regular, clear, and audit-ready status reports for complete peace of mind."
        }
    ],

    // Final CTA
    final_cta_title: "Build Structured & Compliant Workforce Operations Through Strategic Compliance Management Solutions",
    final_cta_description: "Partner with a highly experienced Statutory & Compliance Management Services company delivering workforce compliance support, PF & ESI management, labor law coordination, employee statutory documentation systems, and scalable compliance management solutions for modern businesses.",
    final_cta_primary: "Book Compliance Strategy Session",
    final_cta_secondary: "Request Custom Compliance Management Plan"
};

const ServicePage = () => {
    const { sid } = useParams();
    const navigate = useNavigate();
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const { openEnquiry } = useContext(GlobalEnquiryContext);

    useEffect(() => {
        window.scrollTo(0, 0);

        if (!sid) {
            setLoading(false);
            return;
        }

        // Intercept staffing to load static content directly without backend dependency
        if (sid === 'staffing') {
            setContent(staffingStaticData);
            setLoading(false);
            return;
        }

        // Intercept specialized recruitment to load static content directly without backend dependency
        if (sid === 'specialized-recruitment' || sid === 'specialized') {
            setContent(specializedRecruitmentStaticData);
            setLoading(false);
            return;
        }

        // Intercept RPO to load static content directly without backend dependency
        if (sid === 'rpo' || sid === 'RPO') {
            setContent(rpoStaticData);
            setLoading(false);
            return;
        }

        // Intercept HR Outsourcing to load static content directly without backend dependency
        if (sid === 'hr-outsourcing') {
            setContent(hrOutsourcingStaticData);
            setLoading(false);
            return;
        }

        // Intercept Payroll & Compliance to load static content directly without backend dependency
        if (sid === 'payroll-compliance' || sid === 'payroll' || sid === 'Payroll-Processing') {
            setContent(payrollStaticData);
            setLoading(false);
            return;
        }

        // Intercept Statutory & Compliance to load static content directly without backend dependency
        if (sid === 'statutory-compliance' || sid === 'compliance' || sid === 'statutory') {
            setContent(statutoryStaticData);
            setLoading(false);
            return;
        }

        const fetchServiceData = async () => {
            try {
                const response = await axios.get(`${port}/api/service/${sid}/`);
                setContent(response.data);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching service data:", error);
                setLoading(false);
            }
        };

        fetchServiceData();
    }, [sid]);

    const handleSecondaryCTAClick = () => {
        const el = document.getElementById('specialized-categories');
        if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
        }
    };

    if (loading) {
        return (
            <div className="bg-slate-50 min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!sid) {
        return (
            <div className="bg-white min-h-screen">
                <Helmet>
                    <title>AI-Powered HR Consulting Services | Merida HR Consulting</title>
                    <meta name="description" content="Merida HR Consulting provides specialized HR solutions, contract staffing, permanent recruitment, RPO, payroll outsourcing, and statutory compliance management services across India." />
                    <link rel="canonical" href="https://meridahr.com/service" />
                    <script type="application/ld+json">
                        {JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "Service",
                            "serviceType": "HR Consulting and Staffing Services",
                            "provider": {
                                "@type": "LocalBusiness",
                                "name": "Merida HR Consulting",
                                "address": {
                                    "@type": "PostalAddress",
                                    "streetAddress": "1st Floor, 334/28, 14th Cross Rd, 2nd Block, Jayanagar",
                                    "addressLocality": "Bengaluru",
                                    "addressRegion": "Karnataka",
                                    "postalCode": "560011",
                                    "addressCountry": "IN"
                                }
                            },
                            "areaServed": "IN",
                            "description": "Comprehensive HR solutions including permanent staffing, contract staffing, RPO, HR outsourcing, payroll, and compliance services."
                        })}
                    </script>
                    <script type="application/ld+json">
                        {JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "BreadcrumbList",
                            "itemListElement": [
                                {
                                    "@type": "ListItem",
                                    "position": 1,
                                    "name": "Home",
                                    "item": "https://www.meridahr.com/"
                                },
                                {
                                    "@type": "ListItem",
                                    "position": 2,
                                    "name": "Services",
                                    "item": "https://www.meridahr.com/service"
                                }
                            ]
                        })}
                    </script>
                </Helmet>
                <HomeNavBar />
                <MainServicesPage />
            </div>
        );
    }

    if (!content || content.error) {
        return (
            <div className="bg-slate-50 min-h-screen flex flex-col items-center justify-center">
                <h2 className="text-3xl font-bold text-slate-900 mb-4">Service Not Found</h2>
                <button onClick={() => navigate('/')} className="px-6 py-3 bg-blue-600 text-white rounded-lg">Return Home</button>
            </div>
        );
    }

    const isStaticPage = ['staffing', 'specialized-recruitment', 'specialized', 'rpo', 'RPO', 'hr-outsourcing', 'payroll-compliance', 'payroll', 'Payroll-Processing', 'statutory-compliance', 'compliance', 'statutory'].includes(sid);

    const heroData = {
        title: content.hero_title || content.title,
        subtitle: content.hero_subtitle,
        description: content.hero_description,
        primaryCTA: content.hero_primary_cta || "Get Started Now",
        secondaryCTA: content.hero_secondary_cta || "Contact Us",
        heroImage: content.hero_image
    };

    return (
        <div className="bg-slate-50 min-h-screen">
            <Helmet>
                <title>{content.meta_title || `${content.title} | Merida HR Consulting`}</title>
                <meta name="description" content={content.meta_description || ""} />
                <link rel="canonical" href={`https://meridahr.com/service/${content.slug}`} />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Service",
                        "name": content.title,
                        "provider": {
                            "@type": "LocalBusiness",
                            "name": "Merida HR Consulting",
                            "address": {
                                "@type": "PostalAddress",
                                "streetAddress": "1st Floor, 334/28, 14th Cross Rd, 2nd Block, Jayanagar",
                                "addressLocality": "Bengaluru",
                                "addressRegion": "Karnataka",
                                "postalCode": "560011",
                                "addressCountry": "IN"
                            }
                        },
                        "areaServed": "IN",
                        "description": content.meta_description || content.hero_description
                    })}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": [
                            {
                                "@type": "ListItem",
                                "position": 1,
                                "name": "Home",
                                "item": "https://www.meridahr.com/"
                            },
                            {
                                "@type": "ListItem",
                                "position": 2,
                                "name": "Services",
                                "item": "https://www.meridahr.com/service"
                            },
                            {
                                "@type": "ListItem",
                                "position": 3,
                                "name": content.title,
                                "item": `https://www.meridahr.com/service/${content.slug}`
                            }
                        ]
                    })}
                </script>
            </Helmet>

            <HomeNavBar />

            <ServiceHero
                data={heroData}
                onPrimaryClick={() => openEnquiry(content.enquiry_title || `Connect With Our ${content.title} Specialists`, content.enquiry_fields || [])}
                onSecondaryClick={handleSecondaryCTAClick}
            />

            {content.categories_list && content.categories_list.length > 0 && (
                <ServiceCategories
                    title={content.categories_title}
                    services={content.categories_list.map(c => ({
                        microHeading: c.micro_heading,
                        title: c.title,
                        description: c.description,
                        points: c.points || [],
                        ctaText: c.cta_text || c.ctaText
                    }))}
                    onCategoryClick={(serviceItem) => {
                        const subsid = serviceItem.title.toLowerCase().replace(/&/g, '').replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-').replace(/-+/g, '-');
                        navigate(`/service/${sid}/${subsid}`);
                    }}
                    onCtaClick={() => openEnquiry(content.enquiry_title || `Connect With Our ${content.title} Specialists`, content.enquiry_fields || [])}
                />
            )}

            {content.differentiators_list && content.differentiators_list.length > 0 && (
                <ServiceDifferentiators
                    title={content.differentiators_title}
                    differentiators={content.differentiators_list}
                    onCtaClick={() => openEnquiry(content.enquiry_title || `Connect With Our ${content.title} Specialists`, content.enquiry_fields || [])}
                />
            )}

            {content.process_list && content.process_list.length > 0 && (
                <ExecutionFramework
                    steps={content.process_list.map(s => ({
                        title: s.title,
                        description: s.description
                    }))}
                    onCtaClick={() => openEnquiry(content.enquiry_title || `Connect With Our ${content.title} Specialists`, content.enquiry_fields || [])}
                />
            )}

            {content.industry_list && content.industry_list.length > 0 && (
                <div className="bg-white">
                    <IndustryExpertise
                        title={content.industry_title}
                        desc={content.industry_description}
                        data={content.industry_list.map(i => ({
                            title: i.title,
                            desc: i.description
                        }))}
                        onCtaClick={() => openEnquiry(content.enquiry_title || `Connect With Our ${content.title} Specialists`, content.enquiry_fields || [])}
                    />
                </div>
            )}

            {content.outcomes_list && content.outcomes_list.length > 0 && (
                <OutcomeMetrics
                    title={content.outcomes_title}
                    outcomes={content.outcomes_list}
                    onCtaClick={() => openEnquiry(content.enquiry_title || `Connect With Our ${content.title} Specialists`, content.enquiry_fields || [])}
                />
            )}

            {/* FINAL CTA REPLACED WITH BANNER */}
            <section className="py-16 bg-white relative overflow-hidden border-t border-slate-100">
                <div className="container mx-auto px-6 relative z-10">
                    <div className=" mx-auto text-center">
                        <div className="w-full rounded-[2rem] overflow-hidden shadow-2xl mb-10 border border-slate-200">
                            <img
                                src={recruitmentImg}
                                alt={content.title || "Recruitment Consulting Firm"}
                                className="w-full h-auto object-cover"
                            />
                        </div>
                        <div className="flex justify-center">
                            <CTAButton
                                text={content.final_cta_primary || "Book Consultation"}
                                onClick={() => openEnquiry(content.enquiry_title || `Connect With Our ${content.title} Specialists`, content.enquiry_fields || [])}
                            />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ServicePage;